import { Box, Button, FormControl, FormHelperText, Typography, keyframes } from "@mui/material";
import React from "react";
import { useMenu } from "../../../hook/menu";
import { useCronEditor } from "../../../hook/page/block/cron";
import { CronBlock } from "../../../types/notebook/note/block/cron";
import { isValidCronString } from "../../../util/cron";
import { CopyClipboard } from "../../copyClipboard";
import { DynamicTextField } from "../../dynamicTextField";
import { NoteBlockContainer } from "../../note";
import { SubscriptionSelectMenu } from "../../menu/subscription";

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.65); }
`;

export const CronBlockComponent: React.FC<{ block: CronBlock }> = ({ block }) => {
  const {
    cron,
    name,
    setName,
    schedule,
    setSchedule,
    selectedSub,
    setSelectedSubscription,
  } = useCronEditor(block);
  const shortId = `${block["cron-id"].slice(0, 8)}...`;
  const {
    anchorEl,
    handleOpen,
    onClose,
    open,
  } = useMenu();

  return (
    <Box data-testid="cron-block">
      <SubscriptionSelectMenu
        anchorEl={anchorEl}
        onClose={onClose}
        open={open}
        onChange={setSelectedSubscription}
      />
      <NoteBlockContainer name="Cron">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: cron?.active === "true" ? "success.main" : "text.disabled",
              animation: cron?.active === "true" ? `${pulse} 2s ease-in-out infinite` : "none",
            }}
          />
          <Typography variant="caption" data-testid="cron-status">
            {cron?.active === "true" ? "active" : "inactive"}
          </Typography>
          {cron?.["last-ran"] && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="cron-last-ran"
            >
              last ran {cron["last-ran"]}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "7rem 1fr",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Typography>Name</Typography>
          <DynamicTextField text={name} setText={setName} />
          <Typography>Schedule</Typography>
          <Box>
            <DynamicTextField text={schedule} setText={setSchedule} />
            {schedule.trim() && !isValidCronString(schedule) && (
              <FormHelperText error>
                Invalid cron expression (e.g. */5 * * * *)
              </FormHelperText>
            )}
          </Box>
          <Typography>Subscription</Typography>
          <FormControl>
            <Button onClick={handleOpen} sx={{ justifyContent: "flex-start" }}>
              {selectedSub?.name ?? cron?.["subscription-name"] ?? "Select Subscription"}
            </Button>
          </FormControl>
          <Typography>Status</Typography>
          <Typography>{cron?.active === "true" ? "active" : "inactive"}</Typography>
          <Typography>ID</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography data-testid="cron-id" sx={{ fontFamily: "monospace" }}>
              {shortId}
            </Typography>
            <CopyClipboard value={block["cron-id"]} />
          </Box>
        </Box>
      </NoteBlockContainer>
    </Box>
  );
};
